import classNames from 'classnames';
import * as qs from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import AppPaths from '../../AppPaths';
import FiMiniImage from '../../assets/images/plp-fi-mini.png';
import FiSeries3PlusImage from '../../assets/images/plp-fi-series-three-plus.png';
import FiUltraImage from '../../assets/images/plp-fi-ultra.png';
import { ReactComponent as ArrowRight } from '../../assets/images/icons/arrow_right.svg';
import { ultraIsDiscoverable, useLocale } from '../../contexts/LocaleContext';
import { useIsGrowthBookEnabled } from '../../lib/growthbook';
import useHardwareFirstAddGuard from '../../hooks/useHardwareFirstAddGuard';
import useHardwareFirstPdpPricing from '../../hooks/useHardwareFirstPdpPricing';
import { productListingPage, quizQuestionPage } from '../../lib/analytics/events';
import { CustomerIO } from '../../lib/analytics/customerIO';
import { useLaunchQuiz } from '../../lib/fiQuiz';
import { centsToDollarsNumber } from '../../lib/util';
import { cartActions } from '../../reducers/storeCart';
import * as types from '../../types';
import StarRating from '../StarRating/StarRating';
import Button from '../Button';
import { DesktopImageGallery as MiniImageGallery } from '../MiniPDP/MiniImageGallery/MiniImageGallery';
import UltraDesktopImageGallery from '../UltraPDP/UltraDesktopImageGallery/UltraDesktopImageGallery';
import { DesktopImageGallery as S3ImageGallery } from '../../views/ProductDetails/Series3CollarDetails/components/ImageGallery';
import { Species } from '../../types/gql-op-types';
import HardwareFirstLearnMoreModal from './HardwareFirstLearnMoreModal';
import HardwareFirstPlanModal from './HardwareFirstPlanModal';
import HardwareFirstVariantStep from './HardwareFirstVariantStep';
import useVariantSelector from '../../views/ProductDetails/hooks/useVariantSelector';
import HardwareFirstBelowFold, { PickYourFiDeviceKey, ValuePropBoxes, WhatsInTheBox } from './HardwareFirstBelowFold';
import styles from './HardwareFirstPickYourFi.module.scss';

type DeviceKey = PickYourFiDeviceKey;

// marketing links land on a specific device (/products?device=ultra). aliases cover the
// product ids and spellings an ad build is likely to use alongside the short keys.
const DEVICE_QUERY_ALIASES: Record<string, DeviceKey> = {
  mini: 'mini',
  'fi-mini': 'mini',
  s3: 's3',
  's3-plus': 's3',
  series3: 's3',
  'series-3': 's3',
  'series-3-plus': 's3',
  'smart-collar-v3b': 's3',
  ultra: 'ultra',
  'fi-ultra': 'ultra',
};

function deviceFromQuery(search: string): DeviceKey | undefined {
  const requested = qs.parse(search).device;
  return typeof requested === 'string' ? DEVICE_QUERY_ALIASES[requested.trim().toLowerCase()] : undefined;
}

/** Rewrites only `device`, so a landing url keeps its utm and coupon params. */
function searchWithDevice(search: string, device: DeviceKey): string {
  return qs.stringify({ ...qs.parse(search), device });
}

interface DeviceCard {
  key: DeviceKey;
  name: string;
  image: string;
  thumb: string;
  /** the pdp gallery's lead frame, shown in the learn-more modal hero. */
  modalImage: string;
  tag: string;
  description: string;
  pdpPath: string;
  /** sku added to the cart (and priced for display); undefined = the device needs its PDP (variant choice). */
  addSku?: string;
  /** sku used only to price the card when addSku is undefined (any variant of the product). */
  pricingSku?: string;
  trackShopNow: () => void;
}

function CardPrice({ discount }: { discount: types.HardwareDiscountPreview | undefined }) {
  const { wholeNumberCurrencyFormatter } = useLocale();
  if (!discount) return null;
  return (
    <div className={styles.cardPrice}>
      <span className={styles.discountedPrice}>
        {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(discount.discountedPriceInCents))}
      </span>
      {discount.listPriceInCents > discount.discountedPriceInCents && (
        <span className={styles.listPrice}>
          {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(discount.listPriceInCents))}
        </span>
      )}
    </div>
  );
}

/**
 * Hardware-first replacement for the product listing page: a single "Pick your Fi" selection
 * surface with server-priced hardware cards and a one-time-hardware-price add-to-cart bar.
 * Rendered only for arm-enrolled d2c sessions; control keeps the classic PLP.
 */
export default function HardwareFirstPickYourFi() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const launchQuiz = useLaunchQuiz();
  const { country, currency, wholeNumberCurrencyFormatter } = useLocale();
  const { atCap, routeToBag } = useHardwareFirstAddGuard();
  const products = useSelector((state: types.AppState) => state.config.products);
  const internalTester = useSelector((state: types.AppState) => !!state.config.siteConfig.internalTester);
  // ultra is country- and policy-gated exactly as it is on the classic plp; without this
  // an unsupported market could add an lte/satellite device straight to the bag
  const isUltraAvailable = useIsGrowthBookEnabled() && ultraIsDiscoverable(country, internalTester);
  // mini is preselected so the footer price and below-fold have content on first paint; a
  // ?device= link overrides it once, on mount, after which the shopper's taps own the choice
  const [selectedKey, setSelectedKey] = useState<DeviceKey>(() => deviceFromQuery(location.search) ?? 'mini');

  // mirror the choice into the url so a refresh or a shared link keeps it. `replace`, not
  // `push`: pushing would turn the back button into a device-selection rewind.
  const selectDevice = (device: DeviceKey) => {
    setSelectedKey(device);
    history.replace({ search: searchWithDevice(location.search, device) });
  };
  // desktop puts the primary cta inside the selected card; the docked footer takes
  // over only once that button scrolls out of view (mobile always docks it)
  // s3+ needs a band size and color before it can be added, so its cta opens a
  // variant step rather than going straight to the bag
  const [variantStepOpen, setVariantStepOpen] = useState(false);
  const inCardCtaRef = useRef<HTMLDivElement>(null);
  const [inCardCtaVisible, setInCardCtaVisible] = useState(true);

  const miniProduct = products.find((p) => p.id === types.miniCollarId) as types.IMiniProduct | undefined;
  const s3Product = products.find((p) => p.id === types.series3PlusCollarId) as types.ICollarKitProduct | undefined;
  const ultraProduct = products.find((p) => p.id === types.ultraCollarId) as types.IUltraProduct | undefined;

  const devices: DeviceCard[] = [
    {
      key: 'mini',
      name: 'Fi Mini',
      image: FiMiniImage,
      thumb: '/product_images/mini/device.png',
      modalImage: '/product_images/mini/mini-01-dog@1500w.jpg',
      tag: t('hardware_first_tag_mini'),
      description: t('hardware_first_description_mini'),
      pdpPath: AppPaths.Products('fi-mini') + '?type=dog',
      addSku: miniProduct?.sku,
      trackShopNow: productListingPage.miniShopNow,
    },
    {
      key: 's3',
      name: 'Fi Series 3+',
      image: FiSeries3PlusImage,
      thumb: '/product_images/smart-collar-v3b/gray.png',
      modalImage: '/product_images/smart-collar-v3b/gray1@1500w.jpg',
      tag: t('hardware_first_tag_s3'),
      description: t('hardware_first_description_s3'),
      pdpPath: AppPaths.Products(types.series3PlusCollarId),
      // band size/color live on the PDP, so adding routes there instead of straight to the bag
      pricingSku: s3Product?.variants[0]?.sku,
      trackShopNow: productListingPage.series3PlusShopNow,
    },
    {
      key: 'ultra',
      name: 'Fi Ultra',
      image: FiUltraImage,
      thumb: '/product_images/smart-collar-ultra.png',
      modalImage: '/product_images/ultra/ultra-01@1500w.png',
      tag: t('hardware_first_tag_ultra'),
      description: t('hardware_first_description_ultra'),
      pdpPath: AppPaths.Products('fi-ultra'),
      addSku: ultraProduct?.sku,
      trackShopNow: productListingPage.ultraShopNow,
    },
  ];

  const miniDiscount = useHardwareFirstPdpPricing(devices[0].addSku);
  const s3Discount = useHardwareFirstPdpPricing(devices[1].pricingSku);
  const ultraDiscount = useHardwareFirstPdpPricing(isUltraAvailable ? devices[2].addSku : undefined);
  const discountsByKey: Record<DeviceKey, types.HardwareDiscountPreview | undefined> = {
    mini: miniDiscount,
    s3: s3Discount,
    ultra: ultraDiscount,
  };

  const s3Variants = s3Product?.variants ?? [];
  const noop = () => undefined;
  const { changeVariantOptions, selectedVariant, hasUpdatedSize } = useVariantSelector({
    onColorChange: noop,
    onSelectedVariantChange: noop,
    onSizeChange: noop,
    variants: s3Variants,
    useQueryParams: false,
  });

  const visibleDevices = devices.filter((device) => device.key !== 'ultra' || isUltraAvailable);
  const selected = visibleDevices.find((d) => d.key === selectedKey) ?? visibleDevices[0];
  const selectedDiscount = discountsByKey[selected.key];

  useEffect(() => {
    const node = inCardCtaRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return;
    }
    const observer = new IntersectionObserver((entries) => setInCardCtaVisible(entries.some((e) => e.isIntersecting)));
    observer.observe(node);
    return () => observer.disconnect();
  }, [selected.key]);

  // "Select Fi Mini" / "Select Fi Ultra" / "Select size & color"; the s3 variant step
  // switches to add-to-cart once size and color are being chosen
  const s3StepNeedsSize = selected.key === 's3' && variantStepOpen && !hasUpdatedSize;
  const ctaLabel =
    selected.key === 's3'
      ? variantStepOpen
        ? t('hardware_first_add_to_cart')
        : t('hardware_first_select_size_color')
      : t('hardware_first_select_device', { device: selected.name });

  const onQuiz = () => {
    quizQuestionPage.quizButtonTapped();
    launchQuiz();
  };

  const onAddToCart = () => {
    selected.trackShopNow();
    // s3+ collects size and color first; the variant step's own cta adds to the bag
    if (selected.key === 's3' && !variantStepOpen) {
      setVariantStepOpen(true);
      return;
    }
    const sku = selected.key === 's3' ? selectedVariant?.sku : selected.addSku;
    if (!sku) {
      history.push(selected.pdpPath);
      return;
    }
    // at the unit cap, skip the add the server would reject; the bag's bulk-sales notice explains
    if (!atCap) {
      dispatch(cartActions.addSingleLineCartItem({ sku, currency }));
      CustomerIO.trackAddedItemToCart(
        selected.key === 'mini'
          ? types.miniCollarId
          : selected.key === 's3'
            ? types.series3PlusCollarId
            : types.ultraCollarId,
      );
    }
    routeToBag();
  };

  return (
    <div className={styles.pageBackground}>
      <div className={styles.page}>
        <div className={styles.layout}>
          <div className={styles.hero}>
            {selected.key === 'mini' && <MiniImageGallery species={Species.DOG} />}
            {selected.key === 's3' && <S3ImageGallery selectedVariant={s3Product?.variants[0]} />}
            {selected.key === 'ultra' && <UltraDesktopImageGallery />}
          </div>
          <div className={styles.rail}>
            <h1 className={styles.title}>{t('hardware_first_pick_your_fi')}</h1>
            <div className={styles.subtitle}>{t('hardware_first_pick_your_fi_subtitle')}</div>
            <div className={styles.starRow}>
              <StarRating />
            </div>
            <button className={styles.quizRow} onClick={onQuiz} type="button">
              <span className={styles.quizText}>
                <span className={styles.quizPrompt}>{t('hardware_first_quiz_prompt')}</span>
                <span className={styles.quizCta}>{t('hardware_first_quiz_cta')}</span>
              </span>
              <ArrowRight />
            </button>
            {variantStepOpen && selected.key === 's3' && selectedVariant ? (
              <div className={styles.variantStep}>
                <button className={styles.variantBack} onClick={() => setVariantStepOpen(false)} type="button">
                  {t('back')}
                </button>
                <HardwareFirstVariantStep
                  changeVariantOptions={changeVariantOptions}
                  hasUpdatedSize={hasUpdatedSize}
                  selectedVariant={selectedVariant}
                  variants={s3Variants}
                />
              </div>
            ) : (
              visibleDevices.map((device) => (
                <div
                  key={device.key}
                  className={classNames(styles.card, { [styles.cardSelected]: device.key === selected.key })}
                  onClick={() => selectDevice(device.key)}
                >
                  <div className={styles.cardHeader}>
                    <img className={styles.cardThumb} src={device.thumb} alt={device.name} />
                    <div className={styles.cardName}>{device.name}</div>
                    <CardPrice discount={discountsByKey[device.key]} />
                  </div>
                  <div className={styles.cardDivider} />
                  <div className={styles.cardTag}>{device.tag}</div>
                  <div className={styles.cardDescription}>{device.description}</div>
                  <HardwareFirstLearnMoreModal
                    deviceKey={device.key}
                    name={device.name}
                    image={device.modalImage}
                    discount={discountsByKey[device.key]}
                    onOpen={device.trackShopNow}
                    onSelect={() => {
                      if (device.key === 's3') {
                        history.push(device.pdpPath);
                      } else {
                        selectDevice(device.key);
                      }
                    }}
                    trigger={
                      <button className={styles.learnMore} type="button">
                        {t('learn_more')}
                      </button>
                    }
                  />
                  {device.key === selected.key && (
                    <div className={styles.cardCta} ref={inCardCtaRef}>
                      <Button className={styles.cardCtaButton} disabled={s3StepNeedsSize} onClick={onAddToCart}>
                        {ctaLabel}
                      </Button>
                      <div className={styles.cardCtaNote}>{t('hardware_first_choose_plan_at_activation')}</div>
                    </div>
                  )}
                </div>
              ))
            )}
            <div className={styles.planCard}>
              <div className={styles.planCardRow}>
                <div>
                  <div className={styles.planCardTitle}>{t('hardware_first_plan_card_title')}</div>
                  <div className={styles.planCardBody}>{t('hardware_first_plan_card_body')}</div>
                </div>
                <HardwareFirstPlanModal
                  trigger={
                    <button className={styles.planCardCta} type="button">
                      {t('hardware_first_plan_card_cta')}
                    </button>
                  }
                />
              </div>
            </div>
            <ValuePropBoxes />
            <WhatsInTheBox deviceKey={selected.key} />
          </div>
        </div>
        <HardwareFirstBelowFold deviceKey={selected.key} />
        <div className={classNames(styles.footer, { [styles.footerDocked]: !inCardCtaVisible })}>
          {selectedDiscount && (
            <div className={styles.footerPriceRow}>
              <span className={styles.footerLabel}>{t('hardware_first_one_time_hardware_price')}</span>
              <span className={styles.footerPrice}>
                {selectedDiscount.discountPercent > 0 && (
                  <span className={styles.offPill}>
                    {t('hardware_first_percent_off', { percent: selectedDiscount.discountPercent })}
                  </span>
                )}
                <span className={styles.discountedPrice}>
                  {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(selectedDiscount.discountedPriceInCents))}
                </span>
                {selectedDiscount.listPriceInCents > selectedDiscount.discountedPriceInCents && (
                  <span className={styles.listPrice}>
                    {wholeNumberCurrencyFormatter.format(centsToDollarsNumber(selectedDiscount.listPriceInCents))}
                  </span>
                )}
              </span>
            </div>
          )}
          <Button className={styles.addToCart} disabled={s3StepNeedsSize} onClick={onAddToCart}>
            {ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
